import Modal from "../../../Component/Modal/Modal";
import { useSelector } from "react-redux";
import { useContext, useState } from "react"
import { Checkbox, FormControlLabel, TextField, IconButton, Chip } from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import classes from "./Setting.module.css";
import TemplateSetting from "./TemplateSetting";
import PersonaSetting from "./PersonaSetting";
import MUISelect from "../../../Component/UI/MUISelect/MUISelect";
import { DiscoveryContext } from "../discovery-context";
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import MUIMultiSelect from "../../../Component/UI/MUIMultiSelect/MUIMultiSelect";
import { SITE_SUGGESTIONS } from "../../../utils/constant";

export default function SettingsModal({
    open,
    onClose,
    category,
    categoryWiseMetric,
    metricSettingId,
    onCategoryTemplateChange,
    MetricSettingChangeHandle,
    personaSettings,
    setPersonaSettings,
    rbiType,
    setRbiType,
    keywords,
    setKeywords,
    conversationSettings,
    setConversationSettings,
}) {
    const discoveryCtx = useContext(DiscoveryContext);
    const isFinalTransactionCompleted =
        discoveryCtx?.state.finalTransactionStatus === "COMPLETED" ||
        discoveryCtx?.state.finalTransactionStatus === "RETRY";
    const isDisabled = discoveryCtx?.loading || isFinalTransactionCompleted
    const userMetricSetting = useSelector((store) => store.userSet);
    const templateOptions = userMetricSetting?.data?.map((ele) => ({
        name: ele.name,
        value: ele.id,
        isDefault: ele.isDefault,
    })) ?? [];

    const defaultTemplate = templateOptions.find(opt => opt.isDefault)?.name || "Standard Audit V2";

    const overrideCount = category.filter(cat =>
        categoryWiseMetric[cat] && Number(categoryWiseMetric[cat]) !== Number(metricSettingId)
    ).length;

    const defaultKeywordOptions = ["Top", "Best", "Most Recommended"];
    const [customKeywordInput, setCustomKeywordInput] = useState("");

    const allKeywordOptions = [
        ...defaultKeywordOptions,
        ...keywords.filter(k => !defaultKeywordOptions.includes(k))
    ];

    const handleKeywordToggle = (keyword, checked) => {
        if (checked) {
            if (keywords.length >= 3) return;
            setKeywords(prev => [...prev, keyword]);
        } else {
            setKeywords(prev => prev.filter(k => k !== keyword));
        }
    };

    const handleAddCustomKeyword = () => {
        const trimmed = customKeywordInput.trim();
        if (!trimmed) return;
        if (allKeywordOptions.some(k => k.toLowerCase() === trimmed.toLowerCase())) return;
        if (keywords.length >= 3) {
            setKeywords(prev => [...prev.slice(0, 2), trimmed]);
        } else {
            setKeywords(prev => [...prev, trimmed]);
        }
        setCustomKeywordInput("");
    };

    const handleRemoveCustomKeyword = (keyword) => {
        setKeywords(prev => prev.filter(k => k !== keyword));
    };

    const handleApply = () => {
        onClose();
    };

    const handleSiteChange = (selected) => {
        setConversationSettings(prev => ({ ...prev, selectedSites: selected }));
    };
    return (
        <Modal
            handleClose={onClose}
            open={open}
            title="Configuration Settings"
            width="750"
            showActions={false}
        >
            <div className={classes.modalContent}>
                <div className={classes.modalBody}>
                    {/* Template Settings Section */}
                    <div className={classes.settingSection}>
                        <TemplateSetting
                            templateOptions={templateOptions}
                            metricSettingId={metricSettingId === "default" ? "default" : Number(metricSettingId)}
                            MetricSettingChangeHandle={MetricSettingChangeHandle}
                            category={category}
                            categoryWiseMetric={categoryWiseMetric}
                            onCategoryTemplateChange={onCategoryTemplateChange}
                            defaultTemplate={defaultTemplate}
                            overrideCount={overrideCount}
                        />
                    </div>

                    {/* Persona Settings Section */}
                    <div className={classes.settingSection}>
                        <PersonaSetting
                            personaSettings={personaSettings}
                            setPersonaSettings={setPersonaSettings}
                        />
                    </div>

                    {/* Conversation Settings Section */}
                    <div className={classes.settingSection}>
                        <h4 className={classes.settingSectionTitle}>Conversation</h4>
                         <p className={classes.sectionSubtitle}>
                               These settings apply only during the Final Audit run. Configure site suggestions and content filters that will be used when generating conversation results.
                         </p>
                        <div className={classes.conversationFields}>
                            <MUIMultiSelect
                                disabled={isDisabled || !discoveryCtx?.state?.launchRecord?.length}
                                label={"Site Suggestions"}
                                options={SITE_SUGGESTIONS}
                                value={conversationSettings.selectedSites}
                                customWidth={classes.fullWidth}
                                onChange={handleSiteChange}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={conversationSettings.excludeBrandOwnedSources}
                                        onChange={(e) =>
                                            setConversationSettings(prev => ({
                                                ...prev,
                                                excludeBrandOwnedSources: e.target.checked,
                                            }))
                                        }
                                       disabled={isDisabled ||!discoveryCtx?.state?.launchRecord?.length}
                                    />
                                }
                                label="Eliminate brand content/posts"
                            />
                        </div>
                    </div>

                    {/* RBI Type Settings Section */}
                    <div className={classes.settingSection}>
                        <h4 className={classes.settingSectionTitle}>RBI Type</h4>
                        <MUISelect
                            fullWidth={true}
                            optionValue={rbiType}
                            // label={"RBI Type"}
                            disabled={isDisabled}
                            options={[
                                { name: "Propensity To Recommend", value: "propensityToRecommend" },
                                { name: "Purchase Intent", value: "propensityToBuy" },
                            ]}
                            onChange={(e) => setRbiType(e.target.value)}
                        />
                    </div>

                    {/* Keywords Settings Section */}
                    <div className={classes.settingSection}>
                        <h4 className={classes.settingSectionTitle}>
                            {ENTITY_LABELS.rank} Attributes
                            <span className={classes.keywordCount}>({keywords.length}/3 selected)</span>
                        </h4>
                        <div className={classes.keywordsContainer}>
                            {defaultKeywordOptions.map((keyword) => (
                                <FormControlLabel
                                    key={keyword}
                                    control={
                                        <Checkbox
                                            checked={keywords.includes(keyword)}
                                            onChange={(e) => handleKeywordToggle(keyword, e.target.checked)}
                                            disabled={isDisabled || (!keywords.includes(keyword) && keywords.length >= 3)}
                                            sx={{
                                                color: '#d1d5db',
                                                '&.Mui-checked': { color: '#2dd4bf' },
                                                padding: '4px'
                                            }}
                                        />
                                    }
                                    label={keyword}
                                    sx={{
                                        "&.MuiFormControlLabel-root": {
                                            padding: "0.5rem 1rem",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: "0.75rem",
                                            margin: "0 !important",
                                            background: keywords.includes(keyword) ? "#f0fdfa" : "white",
                                        },
                                        ".MuiFormControlLabel-label": {
                                            fontSize: "0.875rem",
                                            fontWeight: 600,
                                        }
                                    }}
                                />
                            ))}
                        </div>

                        {/* Custom keywords */}
                        {keywords.filter(k => !defaultKeywordOptions.includes(k)).length > 0 && (
                            <div className={classes.customKeywordsChips}>
                                {keywords.filter(k => !defaultKeywordOptions.includes(k)).map((keyword) => (
                                    <Chip
                                        key={keyword}
                                        label={keyword}
                                        onDelete={isDisabled ? undefined : () => handleRemoveCustomKeyword(keyword)}
                                        deleteIcon={<CloseIcon style={{ fontSize: '16px' }} />}
                                        sx={{
                                            background: '#f0fdfa',
                                            border: '1px solid #2dd4bf',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                        }}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Add custom keyword input */}
                        <div className={classes.customKeywordInput}>
                            <TextField
                                size="small"
                                placeholder="Add custom Attribute..."
                                value={customKeywordInput}
                                onChange={(e) => setCustomKeywordInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAddCustomKeyword();
                                    }
                                }}
                                disabled={isDisabled}
                                sx={{ flex: 1 }}
                            />
                            <IconButton
                                onClick={handleAddCustomKeyword}
                                disabled={isDisabled || !customKeywordInput.trim()}
                                sx={{
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.5rem',
                                    color: '#2dd4bf',
                                    '&:hover': { background: '#f0fdfa' }
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}